import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useRevisionGetTtsUrlMutation } from '@app/services/auto-generated/revision'
import { Box, IconButton, Slider, Stack, Typography } from '@common/components'
import { VolumeUpOutlinedIcon } from '@common/icons'
import ieltsQuestions from '../../../mock/ielts.json'
import interviewJobQuestions from '../../../mock/interview-job.json'

export interface InterviewQuestionViewHandle {
  getNewQuestion: () => void
}

interface InterviewQuestionViewProps {
  question?: string
  workshop: string | undefined
}

export const InterviewQuestionView = forwardRef<
  InterviewQuestionViewHandle,
  InterviewQuestionViewProps
>(({ question, workshop }, ref) => {
  const [getTTS] = useRevisionGetTtsUrlMutation()
  const audioRef = useRef<HTMLAudioElement>(null)
  const [sliderValue, setSliderValue] = useState(0.5)

  const getQuestionsList = () => {
    const questions = workshop === 'ielts' ? ieltsQuestions : interviewJobQuestions
    const tolerance = workshop === 'ielts' ? 0.4 : 0.1
    return questions.filter((q) => {
      const score = q.score || 0
      return Math.abs(score - sliderValue) <= tolerance
    })
  }

  const [currentQuestion, setCurrentQuestion] = useState(() => {
    const questions = getQuestionsList()
    const randomIndex = Math.floor(Math.random() * questions.length)
    return questions[randomIndex]
  })

  const getQuestionText = (questionObj: any) => {
    return workshop === 'ielts' ? questionObj?.Question : questionObj?.Question
  }

  const playTTSAudio = async (text: string) => {
    try {
      const { tts_url } = await getTTS({
        ttsRequest: { text, accent: 'en-GB', gender: 'MALE' },
      }).unwrap()
      if (audioRef.current) {
        audioRef.current.src = tts_url
        audioRef.current.play()
      }
    } catch (error) {
      console.error('Error getting TTS:', error)
    }
  }

  const getNewQuestion = (shouldPlayAudio: boolean = true) => {
    const questions = getQuestionsList()
    const randomIndex = Math.floor(Math.random() * questions.length)
    const newQuestion = questions[randomIndex]
    setCurrentQuestion(newQuestion)
    const questionText = getQuestionText(newQuestion)
    if (questionText && shouldPlayAudio) {
      playTTSAudio(questionText)
    }
  }

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setSliderValue(newValue as number)
    getNewQuestion(false)
  }

  const handleSliderChangeCommitted = () => {
    const questions = getQuestionsList()
    const randomIndex = Math.floor(Math.random() * questions.length)
    const newQuestion = questions[randomIndex]
    setCurrentQuestion(newQuestion)
    const questionText = getQuestionText(newQuestion)
    if (questionText) {
      playTTSAudio(questionText)
    }
  }

  useImperativeHandle(ref, () => ({
    getNewQuestion,
  }))

  React.useEffect(() => {
    if (currentQuestion) {
      playTTSAudio(getQuestionText(currentQuestion))
    }
  }, [])

  const handleReplayAudio = () => {
    if (currentQuestion) {
      playTTSAudio(getQuestionText(currentQuestion))
    }
  }

  return (
    <Stack spacing={2} sx={{ width: '100%', maxWidth: 600, p: 1 }}>
      <audio ref={audioRef} />
      <Box
        sx={{ minHeight: '3.5em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Typography
          variant='body1'
          align='center'
          fontWeight={600}
          sx={{
            maxWidth: '90%',
            position: 'absolute',
          }}
        >
          {question || getQuestionText(currentQuestion)}
        </Typography>
      </Box>

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
        spacing={2}
        sx={{
          width: '100%',
          maxWidth: 600,
          position: 'relative',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <Typography sx={{ minWidth: 100 }} color='text.secondary'>
          {workshop === 'ielts' ? 'Part 1' : 'Technical'}
        </Typography>
        <Box sx={{ flex: '0 0 200px' }}>
          <Slider
            value={sliderValue}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            min={0}
            max={1}
            step={0.1}
          />
        </Box>
        <Typography sx={{ minWidth: 100 }} color='text.secondary'>
          {workshop === 'ielts' ? 'Part 2 & 3' : 'Motivational'}
        </Typography>
        <Stack direction='row' spacing={1}>
          <IconButton
            color='primary'
            onClick={handleReplayAudio}
            sx={{
              width: 40,
              height: 40,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            <VolumeUpOutlinedIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Stack>
  )
})

InterviewQuestionView.displayName = 'InterviewQuestionView'
